var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { PatientOnboarding, PatientOnboardingConfirmation, useShowToast, } from "@madmedical/ui";
import { UnderlyingCondition } from "@madmedical/medical";
import { invariant, toggleInArray, uniqueItems } from "@madmedical/utils";
import { ScrollView } from "react-native";
import { useBaseQuestionnaireMutation, useGetMeQuery, useGetUserPregnancyByPatientQuery, } from "../api";
import usePatientId from "../usePatientId";
import useRouteNavigate from "../routing/useRouteNavigate";
import CalculateBirthTimeModalForm from "../pregnancy/CalculateBirthTimeModalForm";
var Step;
(function (Step) {
    Step[Step["Questionnaire"] = 0] = "Questionnaire";
    Step[Step["Consent"] = 1] = "Consent";
})(Step || (Step = {}));
const twinDetails = [
    {
        name: "gender-1",
        label: "Kisbabád neme",
        value: "surprise",
        isBoolean: false,
        isVisible: true,
        twinIndex: 1,
    },
    {
        name: "nameChoose-1",
        label: "Választottál már nevet a kisbabádnak?",
        value: false,
        isBoolean: true,
        isVisible: true,
        twinIndex: 1,
    },
    {
        name: "nameInput-1",
        label: "Kisbabád neve",
        value: "",
        isBoolean: false,
        isVisible: false,
        twinIndex: 1,
    },
];
const defaultPregnantDetails = [
    {
        name: "expectedAt",
        label: "Mi a kisbabád várható születési dátuma?",
        value: new Date().toString(),
        isBoolean: false,
        isVisible: true,
    },
    {
        name: "twins",
        label: "ikreket vársz?",
        value: false,
        isBoolean: true,
        isVisible: false, // Twins story not needded for nowó
    },
    {
        name: "twinsCount",
        label: "Hány magzatod van?",
        value: 1,
        isBoolean: false,
        isVisible: false,
    },
];
let isPregnancyLoaded = false;
const Onboarding = () => {
    var _a, _b, _c, _d, _e, _f;
    const { patientId } = usePatientId();
    const { data: me, refetch: refetchMe } = useGetMeQuery();
    const [step, setStep] = useState(Step.Questionnaire);
    const [underlyings, setUnderlyings] = useState((_b = (_a = me === null || me === void 0 ? void 0 : me.patient) === null || _a === void 0 ? void 0 : _a.underlyingConditions) !== null && _b !== void 0 ? _b : []);
    const [bodyHeight, setBodyHeight] = useState((_d = (_c = me === null || me === void 0 ? void 0 : me.height) === null || _c === void 0 ? void 0 : _c.metric) === null || _d === void 0 ? void 0 : _d.cm);
    const [submit, { isLoading: isSaving }] = useBaseQuestionnaireMutation();
    const showToast = useShowToast();
    const navigate = useRouteNavigate();
    const handleUnderlyingChange = (underlying, selected) => {
        setUnderlyings((prev) => {
            if (prev.includes(underlying) === selected) {
                return prev;
            }
            return toggleInArray(prev, underlying);
        });
    };
    const [pregnantDetails, setPregnantDetails] = useState([
        ...defaultPregnantDetails,
        ...twinDetails,
    ]);
    const [isPregnant, setIsPregnant] = useState(underlyings.includes(UnderlyingCondition.Pregnancy) ? true : false);
    const handlePregnantChange = (selected) => {
        setIsPregnant(selected);
        handleUnderlyingChange(UnderlyingCondition.Pregnancy, selected);
    };
    const { data: pregnancyData } = useGetUserPregnancyByPatientQuery({
        patientId,
    }, {
        skip: !underlyings.includes(UnderlyingCondition.Pregnancy),
    });
    useEffect(() => {
        if (!pregnancyData || !isPregnancyLoaded) {
            return;
        }
        const { expectedAt, infants } = pregnancyData;
        const getValue = (name, infant) => {
            var _a, _b;
            if (!infant) {
                return null;
            }
            if (name.startsWith("gender")) {
                return (_a = infant.gender) !== null && _a !== void 0 ? _a : "surprise";
            }
            if (name.startsWith("nameInput")) {
                return (_b = infant.givenName) !== null && _b !== void 0 ? _b : "";
            }
            if (name.startsWith("nameChoose")) {
                return !!infant.givenName;
            }
        };
        const transformedTwins = infants.flatMap((infant, index) => twinDetails.map((detail) => (Object.assign(Object.assign({}, detail), { id: infant.id, twinIndex: index + 1, name: `${detail.name.split("-")[0]}-${index + 1}`, value: getValue(detail.name, infant), isVisible: true }))));
        setPregnantDetails(() => [
            {
                name: "expectedAt",
                label: "Mi a kisbabád várható születési dátuma?",
                value: expectedAt,
                isBoolean: false,
                isVisible: true,
            },
            {
                name: "twins",
                label: "ikreket vársz?",
                value: infants.length > 1,
                isBoolean: true,
                isVisible: infants.length > 1,
            },
            {
                name: "twinsCount",
                label: "Hány magzatod van?",
                value: infants.length,
                isBoolean: false,
                isVisible: infants.length > 1,
            },
            ...transformedTwins,
        ]);
        setTimeout(() => {
            isPregnancyLoaded = true;
        }, 1000);
    }, [pregnancyData]);
    const handlePregnantDetailsChange = useCallback((name, value, isVisible) => {
        setPregnantDetails((details) => details.map((detail) => detail.name === name
            ? Object.assign(Object.assign({}, detail), { value: value !== null && value !== void 0 ? value : detail.value, isVisible: isVisible !== null && isVisible !== void 0 ? isVisible : detail.isVisible }) : detail));
    }, []);
    useEffect(() => {
        var _a, _b, _c, _d;
        if (!pregnantDetails || pregnantDetails.length === 0 || !isPregnant) {
            return;
        }
        const isTwinsSelected = (_b = (_a = pregnantDetails === null || pregnantDetails === void 0 ? void 0 : pregnantDetails.find((detail) => detail.name === "twins")) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : false;
        let isTwinCount = (_c = pregnantDetails === null || pregnantDetails === void 0 ? void 0 : pregnantDetails.find((detail) => detail.name === "twinsCount")) !== null && _c !== void 0 ? _c : {
            name: "twinsCount",
            value: 1,
            isBoolean: false,
            isVisible: false,
        };
        if ((isTwinCount === null || isTwinCount === void 0 ? void 0 : isTwinCount.value) === 1 && isTwinsSelected) {
            isTwinCount = Object.assign(Object.assign({}, isTwinCount), { value: 2 });
            setPregnantDetails((details) => details.map((detail) => detail.name === "twinsCount" ? isTwinCount : detail));
        }
        if (isTwinsSelected !== (isTwinCount === null || isTwinCount === void 0 ? void 0 : isTwinCount.isVisible)) {
            if (!isTwinsSelected) {
                const filteredItems = pregnantDetails.filter((detail) => {
                    if (detail.twinIndex && detail.twinIndex > 1) {
                        return false;
                    }
                    return true;
                });
                setPregnantDetails(filteredItems);
            }
            handlePregnantDetailsChange("twinsCount", null, isTwinsSelected);
        }
        if (isTwinsSelected &&
            isTwinCount &&
            (pregnantDetails.every((item) => item.twinIndex != +isTwinCount.value) ||
                ((_d = pregnantDetails === null || pregnantDetails === void 0 ? void 0 : pregnantDetails[pregnantDetails.length - 1]) === null || _d === void 0 ? void 0 : _d.twinIndex) > +isTwinCount.value)) {
            const filteredItems = pregnantDetails.filter((detail) => {
                if (detail.twinIndex && detail.twinIndex > +isTwinCount.value) {
                    return false;
                }
                return true;
            });
            const newItems = Array(+(isTwinCount === null || isTwinCount === void 0 ? void 0 : isTwinCount.value) - 1)
                .fill("")
                .map((_, index) => {
                const newItem = [...twinDetails].map((detail) => {
                    const newDetail = Object.assign({}, detail);
                    newDetail.name = `${detail.name.split("-")[0]}-${index + 2}`;
                    newDetail.twinIndex = index + 2;
                    return newDetail;
                });
                return newItem;
            })
                .flat();
            setPregnantDetails(uniqueItems([...filteredItems, ...newItems], "name"));
        }
        pregnantDetails.forEach((detail) => {
            if (detail.name.includes("nameChoose")) {
                const detailIndex = detail.name.split("-")[1];
                const isNameSelected = detail.value;
                const isNameInput = pregnantDetails === null || pregnantDetails === void 0 ? void 0 : pregnantDetails.find((input) => input.name === `nameInput-${detailIndex}`);
                if (isNameSelected !== (isNameInput === null || isNameInput === void 0 ? void 0 : isNameInput.isVisible)) {
                    handlePregnantDetailsChange(`nameInput-${detailIndex}`, null, isNameSelected);
                }
            }
        });
        isPregnancyLoaded = true;
    }, [pregnantDetails, handlePregnantDetailsChange, isPregnant]);
    const [calculateBirthTime, setCalculateBirthTime] = useState(false);
    const calculateBirthTimeValues = (lastMenstruationDate, menstruationCycleLength) => {
        const totalDays = 280;
        const expectedDate = new Date(lastMenstruationDate);
        if (menstruationCycleLength === 28) {
            expectedDate.setDate(expectedDate.getDate() + totalDays);
        }
        if (menstruationCycleLength > 28) {
            const difference = menstruationCycleLength - 28;
            expectedDate.setDate(expectedDate.getDate() + difference + totalDays);
        }
        if (menstruationCycleLength < 28) {
            const difference = 28 - menstruationCycleLength;
            expectedDate.setDate(expectedDate.getDate() - difference + totalDays);
        }
        setPregnantDetails((details) => details.map((detail) => detail.name === "expectedAt"
            ? Object.assign(Object.assign({}, detail), { value: expectedDate.toString() }) : detail));
        setCalculateBirthTime(false);
    };
    const handleSubmit = () => {
        var _a;
        invariant(bodyHeight);
        const infants = [
            {
                twinIndex: 1,
            },
        ];
        for (const item of pregnantDetails) {
            const itemName = item.name;
            const twinIndex = item.twinIndex;
            const existingInfant = infants.find((infant) => infant.twinIndex == twinIndex);
            if (!existingInfant) {
                const newInfant = { twinIndex: twinIndex };
                if (itemName.startsWith("gender-")) {
                    const genderValue = item.value;
                    if (genderValue !== "surprise") {
                        newInfant.gender = genderValue;
                    }
                }
                else if (itemName.startsWith("nameChoose-") &&
                    item.value === true) {
                    const correspondingNameInput = pregnantDetails.find((inputItem) => 
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    (inputItem === null || inputItem === void 0 ? void 0 : inputItem.name) === `nameInput-${twinIndex}`);
                    if (correspondingNameInput) {
                        newInfant.givenName =
                            correspondingNameInput.value;
                    }
                }
                infants.push(newInfant);
            }
            if (itemName.startsWith("gender-") && existingInfant) {
                const genderValue = item.value;
                if (genderValue !== "surprise") {
                    existingInfant.gender = genderValue;
                }
            }
            else if (itemName.startsWith("nameChoose-") &&
                item.value === true &&
                existingInfant) {
                const correspondingNameInput = pregnantDetails.find(
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                (inputItem) => (inputItem === null || inputItem === void 0 ? void 0 : inputItem.name) === `nameInput-${twinIndex}`);
                if (correspondingNameInput) {
                    existingInfant.givenName =
                        correspondingNameInput.value;
                }
            }
        }
        const expectedAtValue = (_a = pregnantDetails.find((item) => item.name === "expectedAt")) === null || _a === void 0 ? void 0 : _a.value;
        const expectedAtDate = new Date(expectedAtValue);
        const pregnancyData = {
            expectedAt: expectedAtValue
                ? expectedAtDate.toISOString()
                : undefined,
            infants: infants.filter((item) => item.twinIndex !== undefined),
            isPregnant,
        };
        void (() => __awaiter(void 0, void 0, void 0, function* () {
            yield submit({
                patientId,
                underlyingConditions: underlyings,
                bodyHeightCm: bodyHeight,
                pregnancyData: isPregnant
                    ? pregnancyData
                    : undefined,
            }).unwrap();
            showToast("Sikeres mentés.", { variant: "success" });
            yield refetchMe();
            navigate("dashboard", {}, { replace: true });
        }))();
    };
    // This should happen on backend but this place is not using forms… TODO: Rethink at some point
    const isBodyHeightValid = bodyHeight && bodyHeight > 30 && bodyHeight < 250;
    const packages = ((_f = (_e = me === null || me === void 0 ? void 0 : me.patient) === null || _e === void 0 ? void 0 : _e.packages) !== null && _f !== void 0 ? _f : []);
    const isPregnancyPackage = !!((me === null || me === void 0 ? void 0 : me.patient) && packages.some((item) => (item === null || item === void 0 ? void 0 : item.type) === "pregnancy"));
    const isInsulinPackage = !!((me === null || me === void 0 ? void 0 : me.patient) && packages.some((item) => (item === null || item === void 0 ? void 0 : item.type) === "diabetes"));
    switch (step) {
        case Step.Questionnaire:
            return (_jsxs(_Fragment, { children: [_jsx(ScrollView, { children: _jsx(PatientOnboarding, { bodyHeight: bodyHeight, selectedUnderlyings: underlyings, onUnderlyingChange: handleUnderlyingChange, onBodyHeightChange: setBodyHeight, onNextPress: handleSubmit, isNextDisabled: !isBodyHeightValid, pregnantDetails: pregnantDetails, onPregnantDetailsChange: handlePregnantDetailsChange, openCalculateBirthTimeModal: () => setCalculateBirthTime(true), isPregnant: isPregnant, setIsPregnant: handlePregnantChange, patient: me === null || me === void 0 ? void 0 : me.patient, packages: {
                                pregnancy: isPregnancyPackage,
                                diabetes: isInsulinPackage,
                            } }) }), _jsx(CalculateBirthTimeModalForm, { open: calculateBirthTime, onClose: () => setCalculateBirthTime(false), transmittedValues: calculateBirthTimeValues })] }));
        case Step.Consent:
            return (_jsx(ScrollView, { children: _jsx(PatientOnboardingConfirmation, { onPreviousPress: () => {
                        setStep(Step.Questionnaire);
                    }, onNextPress: handleSubmit, isSaving: isSaving }) }));
    }
};
export default Onboarding;
